import type { DocumentRequest, DocumentContent } from "@perceptron/api/apiContracts/document";
import { shuffle } from "lodash-es";

import type { Mode } from "@demo/constants/mode";
import { env } from "@demo/env";
import { AVAILABLE_MODELS } from "@demo/shared/types";
import { UUID } from "@demo/types/base";

import { BASE_ASSETS } from "./constants";
import {
  AllowedImageExtension,
  AllowedVideoExtension,
  TemplateAssetKey,
  TemplateCategory,
  TemplateDefinition,
} from "./types";

// Built from BASE_ASSETS so that any UUID declared there — including
// thumbnails that aren't in a template's assets array (e.g. video posters) —
// resolves through getTemplateAssetCdnUrl.
const BASE_ASSET_LIST = Object.values(BASE_ASSETS);

export const TEMPLATE_ASSET_UUIDS: ReadonlySet<string> = (() => {
  const s = new Set<string>();
  for (const a of BASE_ASSET_LIST) {
    if (s.has(a.uuid)) {
      console.warn(`Duplicate template asset uuid ${a.uuid} in BASE_ASSETS`);
    }
    s.add(a.uuid);
  }
  return s;
})();

export const TEMPLATE_UUID_TO_KEY: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const a of BASE_ASSET_LIST) {
    m.set(a.uuid, a.key);
  }
  return m;
})();

export function getTemplateAssetCdnUrl(id: UUID): string {
  const base = env.NEXT_PUBLIC_TEMPLATE_ASSETS_CDN_BASE_URL;
  if (!base) {
    throw new Error("NEXT_PUBLIC_TEMPLATE_ASSETS_CDN_BASE_URL is missing");
  }
  const key = TEMPLATE_UUID_TO_KEY.get(id);
  if (!key) {
    throw new Error(`Unknown template asset uuid ${id}`);
  }
  return `${base}/${key}`;
}

// Utility to collect image UUIDs from a DocumentRequest (for UUID[] → URL[] backend calls)
export function collectImageUuids(req: DocumentRequest): Array<UUID> {
  const out: Array<UUID> = [];
  function scan(contents: Array<DocumentContent>) {
    for (const c of contents) {
      if (c.type === "images") {
        for (const img of c.value) {
          out.push(img.uuid);
        }
      }
    }
  }
  scan(req.system);
  scan(req.user);
  return out;
}

// Check if a UUID is a template asset
export function isTemplateAsset(uuid: UUID): boolean {
  return TEMPLATE_ASSET_UUIDS.has(uuid);
}

export function extractExtensionFromKey(key: TemplateAssetKey): AllowedImageExtension | AllowedVideoExtension {
  const extension = key.split(".").pop() as AllowedImageExtension | AllowedVideoExtension;
  return extension;
}

export function getTemplateCategory(template: TemplateDefinition): TemplateCategory {
  return template.assets.some((a) => a.mime.startsWith("video/")) ? "video" : "image";
}

export function getFeaturedTemplates(templates: ReadonlyArray<TemplateDefinition>): Array<TemplateDefinition> {
  const featured = templates.filter((t) => t.featured);

  // Lead with a randomly-picked video (if any) and shuffle the rest behind it.
  const [lead, ...remainingVideos] = shuffle(featured.filter((t) => getTemplateCategory(t) === "video"));
  if (!lead) return shuffle(featured);

  const nonVideos = featured.filter((t) => getTemplateCategory(t) !== "video");
  return [lead, ...shuffle([...remainingVideos, ...nonVideos])];
}

// Within the featured and ordinary groups, prefer the current default model.
const DEFAULT_MODEL_NAMES: ReadonlySet<string> = new Set(
  AVAILABLE_MODELS.filter((m) => m.isDefault).map((m) => m.name),
);

const getModelDisplayPriority = (t: TemplateDefinition): number =>
  t.mode === "compose" && DEFAULT_MODEL_NAMES.has(t.request.model) ? 0 : 1;

/**
 * Filters examples to a single mode. The gallery sources its list through this
 * so each mode shows only its own examples (the preview components are mode-agnostic).
 */
export function getTemplatesForMode(
  templates: ReadonlyArray<TemplateDefinition>,
  mode: Mode,
): ReadonlyArray<TemplateDefinition> {
  return templates.filter((t) => t.mode === mode);
}

export function getTemplatesByCategory(
  templates: ReadonlyArray<TemplateDefinition>,
  category: TemplateCategory,
): Array<TemplateDefinition> {
  return templates
    .filter((t) => getTemplateCategory(t) === category)
    .sort(
      (a, b) => Number(!!b.featured) - Number(!!a.featured) || getModelDisplayPriority(a) - getModelDisplayPriority(b),
    );
}
